// src/components/customer/ReviewModal.jsx
import React, { useState, useEffect } from "react";
import { FiX, FiStar } from "react-icons/fi";
import Spinner from "../common/Spinner";

export default function ReviewModal({
  open,
  productName,
  productImage,
  initialRating = 0,
  initialComment = "",
  submitting = false,
  onClose,
  onSubmit,
}) {
  const [rating,  setRating]  = useState(initialRating);
  const [hover,   setHover]   = useState(0);
  const [comment, setComment] = useState(initialComment);

  // Re-sync whenever a different item's modal is opened (or reopened to edit)
  useEffect(() => {
    if (open) {
      setRating(initialRating);
      setComment(initialComment);
      setHover(0);
    }
  }, [open, initialRating, initialComment]);

  if (!open) return null;

  const handleSubmit = () => {
    if (rating < 1) return;
    onSubmit(rating, comment.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md p-5 sm:p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <FiX className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0">
            {productImage
              ? <img src={productImage} alt={productName} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-xl">🌱</div>}
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 text-sm leading-snug truncate">{productName}</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {initialRating ? "Edit your review" : "Rate your experience"}
            </p>
          </div>
        </div>

        {/* Star picker */}
        <div className="flex items-center justify-center gap-1.5 mb-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="p-1"
            >
              <FiStar
                className={`w-8 h-8 transition-colors ${
                  star <= (hover || rating)
                    ? "text-amber-400 fill-amber-400"
                    : "text-gray-200"
                }`}
              />
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mb-4">
          {rating > 0 ? `${rating} out of 5` : "Tap a star to rate"}
        </p>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder="Share your experience with this product (optional)"
          className="input resize-none w-full"
        />

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={rating < 1 || submitting}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <><Spinner size="sm" /> Saving...</> : initialRating ? "Update Review" : "Submit Review"}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}